/**
 * «Бот для клиентов» — верификация публичных карточек операторов. Общий
 * компонент: переиспользуется и в OO-шелле (sec-nav «Бот для клиентов»), и в
 * EO-шелле (вкладка «Одобрение»). RBAC actor_approval (обе роли).
 *
 * Две части (sub-nav, фиолетовый per A2/C3):
 *  а) ЛЕНТА ИЗМЕНЕНИЙ (главное) — pending-заявки, сгруппированы по оператору,
 *     summary[] + тап → diff было/станет + предпросмотр карточки; инлайн
 *     Одобрить / Отклонить / На доработку (причина — кастомная модалка, A3).
 *     Опционально массовое «Одобрить всё» оверлеем (C13).
 *  б) АККАУНТЫ — список операторов, cardStatus, индикатор hasPending, тумблер
 *     авто-одобрения (PUT), «Посмотреть как сейчас» → предпросмотр оверлеем.
 *
 * Цвет роли — через --role (cyan у OO, фуксия у EO); статусы: pending=orange,
 * approved=green, rejected=red, needs_revision=blue. SVG-иконки, кастомные
 * модалки, числа/шрифты — токены. UTC+5 даты приходят строками с бэка.
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactElement } from "react";
import {
  useActorChanges, type ActorChangeFeedItem, type ActorChangeDetail, type ChangeStatus, type DiffField,
} from "../../hooks/useActorChanges";
import {
  useOperatorsAdmin, type OperatorAccount, type OperatorProfilePreview, type MediaAsset, type CardStatus,
} from "../../hooks/useOperatorsAdmin";
import { useUcConfig, type UcConfig } from "../../hooks/useUcConfig";
import {
  useMaterialsAdmin, type MaterialsIssue, type ObjClass,
  type MaterialsAfisha, type MaterialsObject, type MaterialsProtocol,
} from "../../hooks/useMaterialsAdmin";
import {
  NavFeed, NavAccounts, ActApprove, ActReject, ActRevise, Eye, Xmark, Person,
  Photo, VideoIco, News, DocLines, Lock, ActCheck, WarnTri, Plus, ActTrash,
} from "./v8icons";
import css from "./clientbot.module.css";

type SubTab = "changes" | "operators" | "economy" | "materials";

/* Монета (UTC) — локальная иконка для под-вкладки «Экономика». */
function Coin(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.4v9.2M14.3 9.1c-.5-.8-1.4-1.2-2.3-1.2-1.4 0-2.4.8-2.4 1.9 0 2.6 5 1.2 5 3.8 0 1.1-1.1 1.9-2.6 1.9-1.1 0-2-.5-2.5-1.3" />
    </svg>
  );
}

/* Журнал — локальная иконка для под-вкладки «Материалы» (Вестник). */
function Journal(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M5 4h11a2 2 0 0 1 2 2v14H7a2 2 0 0 1-2-2z" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </svg>
  );
}

/* ── UTC+5 time formatting (даты с бэка уже ISO; режем до ЧЧ:ММ / ДД.ММ) ──── */
function hhmm(iso: string | null): string {
  if (!iso) return "";
  const m = /T(\d{2}:\d{2})/.exec(iso);
  if (m) return m[1]!;
  const t = iso.length >= 16 ? iso.slice(11, 16) : iso;
  return t;
}
function ddmm(iso: string | null): string {
  if (!iso) return "";
  const m = /(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}` : "";
}

const STATUS_LABEL: Record<ChangeStatus, string> = {
  pending: "на проверке",
  approved: "одобрено",
  rejected: "отклонено",
  needs_revision: "на доработке",
};
const STATUS_CLS: Record<ChangeStatus, string> = {
  pending: css.stPending ?? "",
  approved: css.stApproved ?? "",
  rejected: css.stRejected ?? "",
  needs_revision: css.stRevision ?? "",
};

const REJECT_PRESETS = ["не по гайдлайну", "плохое качество фото", "некорректный текст", "нарушает правила"];
const REVISION_PRESETS = ["уточни описание", "замени фото", "поправь заголовок новости", "добавь деталей"];

/* ── Public component ─────────────────────────────────────────────────────── */
export function ClientBotPanel() {
  const [tab, setTab] = useState<SubTab>("changes");
  const changes = useActorChanges();
  const ops = useOperatorsAdmin();
  const [toast, setToast] = useState<string | null>(null);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  return (
    <div className={css.root}>
      <div className={css.subnav}>
        <button type="button" className={`${css.subtab} ${tab === "changes" ? css.subtabOn : ""}`} onClick={() => setTab("changes")}>
          <NavFeed aria-hidden="true" /> Лента
          {changes.count > 0 ? <span className={css.cnt}>{changes.count}</span> : null}
        </button>
        <button type="button" className={`${css.subtab} ${tab === "operators" ? css.subtabOn : ""}`} onClick={() => setTab("operators")}>
          <NavAccounts aria-hidden="true" /> Аккаунты
        </button>
        <button type="button" className={`${css.subtab} ${tab === "economy" ? css.subtabOn : ""}`} onClick={() => setTab("economy")}>
          <Coin aria-hidden="true" /> Экономика
        </button>
        <button type="button" className={`${css.subtab} ${tab === "materials" ? css.subtabOn : ""}`} onClick={() => setTab("materials")}>
          <Journal aria-hidden="true" /> Вестник
        </button>
      </div>

      {tab === "changes" ? (
        <ChangesFeed changes={changes} onToast={flash} />
      ) : tab === "operators" ? (
        <OperatorsList ops={ops} onToast={flash} />
      ) : tab === "economy" ? (
        <EconomyPanel onToast={flash} />
      ) : (
        <MaterialsPanel onToast={flash} />
      )}

      {toast ? <div className={css.toast}>{toast}</div> : null}
    </div>
  );
}

/* ── а) Лента изменений ───────────────────────────────────────────────────── */
function ChangesFeed({ changes, onToast }: { changes: ReturnType<typeof useActorChanges>; onToast: (m: string) => void }) {
  const { items, count, loading, error, reload, fetchDetail, approve, reject, needsRevision } = changes;
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ActorChangeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [noteFor, setNoteFor] = useState<{ id: number; kind: "reject" | "revision" } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [batch, setBatch] = useState<Set<number>>(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Reset batch whenever the feed reloads.
  useEffect(() => { setBatch(new Set()); }, [count]);

  const openDetail = useCallback(async (id: number) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id);
    setDetail(null);
    setDetailLoading(true);
    const d = await fetchDetail(id);
    setDetail(d);
    setDetailLoading(false);
  }, [openId, fetchDetail]);

  const doApprove = useCallback(async (id: number) => {
    setBusyId(id);
    const res = await approve(id);
    setBusyId(null);
    onToast(res.ok ? "Опубликовано" : `Ошибка: ${res.error ?? "—"}`);
    if (res.ok && openId === id) { setOpenId(null); setDetail(null); }
  }, [approve, onToast, openId]);

  const submitNote = useCallback(async (note: string) => {
    if (!noteFor) return;
    const { id, kind } = noteFor;
    setNoteFor(null);
    setBusyId(id);
    const res = kind === "reject" ? await reject(id, note) : await needsRevision(id, note);
    setBusyId(null);
    onToast(res.ok ? (kind === "reject" ? "Отклонено" : "Отправлено на доработку") : `Ошибка: ${res.error ?? "—"}`);
    if (res.ok && openId === id) { setOpenId(null); setDetail(null); }
  }, [noteFor, reject, needsRevision, onToast, openId]);

  const toggleBatch = useCallback((id: number) => {
    setBatch((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const runBulkApprove = useCallback(async () => {
    setBulkBusy(true);
    let okN = 0;
    for (const id of batch) {
      const res = await approve(id);
      if (res.ok) okN += 1;
    }
    setBulkBusy(false);
    setBulkConfirm(false);
    setBatch(new Set());
    onToast(`Одобрено: ${okN}`);
  }, [batch, approve, onToast]);

  // Group items by operator (nick) — preserves first-seen order.
  const groups = useMemo(() => {
    const map = new Map<string, { operator: string; items: ActorChangeFeedItem[] }>();
    for (const it of items) {
      const key = it.staffId || it.operator;
      const g = map.get(key) ?? { operator: it.operator, items: [] };
      g.items.push(it);
      map.set(key, g);
    }
    return [...map.values()];
  }, [items]);

  if (loading) {
    return (
      <div className={css.content}>
        {[0, 1, 2].map((i) => (
          <div key={i} className={css.skel}>
            <div className={css.skelLine} style={{ width: "55%" }} />
            <div className={css.skelLine} style={{ width: "88%" }} />
            <div className={css.skelLine} style={{ width: "40%" }} />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={css.emptyState}>
        <span className={css.esIco}><WarnTri /></span>
        <span className={css.esT}>Не удалось загрузить ленту</span>
        <span className={css.esS}>{error}</span>
        <button type="button" className={css.esBtn} onClick={reload}>Повторить</button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={css.emptyState}>
        <span className={`${css.esIco} ${css.green}`}><ActCheck width={40} height={40} /></span>
        <span className={`${css.esT} ${css.green}`}>Заявок нет</span>
        <span className={css.esS}>Все правки операторов разобраны.</span>
      </div>
    );
  }

  const showBulk = batch.size > 0;

  return (
    <>
      <div className={`${css.content} ${showBulk ? css.contentBulk : ""}`}>
        <div className={css.toolbar}>
          <span className={css.tbInfo}><span className={css.n}>{count}</span> {changesWord(count)} на проверке</span>
          {batch.size > 0 ? (
            <button type="button" className={css.tbBtn} onClick={() => setBulkConfirm(true)}>
              <ActApprove /> Одобрить выбранные
            </button>
          ) : null}
        </div>

        {groups.map((g) => (
          <div key={g.operator} className={css.group}>
            <div className={css.groupHead}>
              <span className={css.groupAvatar}><Person /></span>
              <span className={css.groupNick}>{g.operator}</span>
              <span className={css.groupCount}>{g.items.length} {changesWord(g.items.length)}</span>
            </div>
            {g.items.map((it) => (
              <ChangeCard
                key={it.id}
                item={it}
                open={openId === it.id}
                detail={openId === it.id ? detail : null}
                detailLoading={openId === it.id && detailLoading}
                busy={busyId === it.id}
                batchOn={batch.has(it.id)}
                batchMode={showBulk}
                onToggleBatch={() => toggleBatch(it.id)}
                onOpen={() => openDetail(it.id)}
                onApprove={() => doApprove(it.id)}
                onReject={() => setNoteFor({ id: it.id, kind: "reject" })}
                onRevise={() => setNoteFor({ id: it.id, kind: "revision" })}
              />
            ))}
          </div>
        ))}
      </div>

      {showBulk ? (
        <div className={css.bulkBar}>
          <span className={css.bbInfo}><span className={css.n}>{batch.size}</span> выбрано</span>
          <button type="button" className={`${css.bbBtn} ${css.bbCancel}`} onClick={() => setBatch(new Set())}>Снять</button>
          <button type="button" className={`${css.bbBtn} ${css.bbAccept}`} onClick={() => setBulkConfirm(true)}>Одобрить {batch.size}</button>
        </div>
      ) : null}

      {noteFor ? (
        <NoteModal
          kind={noteFor.kind}
          onCancel={() => setNoteFor(null)}
          onSubmit={submitNote}
        />
      ) : null}

      {bulkConfirm ? (
        <NoteConfirm
          title={`Одобрить ${batch.size} ${changesWord(batch.size)}`}
          body={`Все выбранные правки будут опубликованы сразу. Действие применяется к каждой заявке.`}
          confirmLabel="Одобрить всё"
          busy={bulkBusy}
          onCancel={() => setBulkConfirm(false)}
          onConfirm={runBulkApprove}
        />
      ) : null}
    </>
  );
}

function ChangeCard({
  item, open, detail, detailLoading, busy, batchOn, batchMode,
  onToggleBatch, onOpen, onApprove, onReject, onRevise,
}: {
  item: ActorChangeFeedItem;
  open: boolean;
  detail: ActorChangeDetail | null;
  detailLoading: boolean;
  busy: boolean;
  batchOn: boolean;
  batchMode: boolean;
  onToggleBatch: () => void;
  onOpen: () => void;
  onApprove: () => void;
  onReject: () => void;
  onRevise: () => void;
}) {
  return (
    <article className={`${css.card} ${batchOn ? css.selBatch : ""}`}>
      <div className={css.cardHead}>
        {batchMode ? (
          <button type="button" className={`${css.cardCb} ${batchOn ? css.on : ""}`} aria-label="Выбрать" aria-pressed={batchOn} onClick={onToggleBatch}>
            <ActCheck />
          </button>
        ) : null}
        <span className={css.cardTime}>{ddmm(item.submittedAt)} {hhmm(item.submittedAt)}</span>
        <span className={`${css.cardStatus} ${STATUS_CLS[item.status]}`}>{STATUS_LABEL[item.status]}</span>
      </div>

      <div className={css.summary}>
        {item.summary.length === 0 ? (
          <span className={css.sumChip}><DocLines /> Правка карточки</span>
        ) : (
          item.summary.map((s, i) => (
            <span key={i} className={css.sumChip}>{summaryIcon(s)} {s}</span>
          ))
        )}
      </div>

      {item.reviewNote ? (
        <div className={css.reviewNote}><WarnTri /> {item.reviewNote}</div>
      ) : null}

      <div className={css.actions}>
        <button type="button" className={`${css.act} ${css.actApprove}`} disabled={busy} onClick={onApprove}>
          <ActApprove /> Одобрить
        </button>
        <button type="button" className={`${css.act} ${css.actReject}`} disabled={busy} onClick={onReject}>
          <ActReject /> Отклонить
        </button>
        <button type="button" className={`${css.act} ${css.actRevise}`} disabled={busy} onClick={onRevise}>
          <ActRevise /> На доработку
        </button>
        <button type="button" className={css.actView} aria-label={open ? "Скрыть" : "Показать"} onClick={onOpen}>
          <Eye />
        </button>
      </div>

      {open ? (
        detailLoading ? (
          <div className={css.diff}><div className={css.diffEmpty}>Загрузка изменений…</div></div>
        ) : detail ? (
          <>
            <DiffBlock diff={detail.diff} />
            <CardPreview
              profile={(detail.payload.profile ?? detail.current ?? {}) as Record<string, unknown>}
              news={detail.payload.news ?? []}
              media={[]}
            />
          </>
        ) : (
          <div className={css.diff}><div className={css.diffEmpty}>Нет данных изменения</div></div>
        )
      ) : null}
    </article>
  );
}

/* ── diff было/станет ─────────────────────────────────────────────────────── */
function DiffBlock({ diff }: { diff: Record<string, DiffField> }) {
  const entries = Object.entries(diff ?? {});
  if (entries.length === 0) {
    return <div className={css.diff}><div className={css.diffH}>Изменения</div><div className={css.diffEmpty}>Поля не изменились</div></div>;
  }
  return (
    <div className={css.diff}>
      <div className={css.diffH}>Было / станет</div>
      {entries.map(([field, { before, after }]) => (
        <div key={field} className={css.diffRow}>
          <span className={css.diffField}>{fieldLabel(field)}</span>
          <div className={css.diffPair}>
            <span className={`${css.diffBefore} ${isEmpty(before) ? css.diffEmpty : ""}`}>{fmtVal(before)}</span>
            <span className={css.diffArr}>→</span>
            <span className={`${css.diffAfter} ${isEmpty(after) ? css.diffEmpty : ""}`}>{fmtVal(after)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── б) Аккаунты операторов ───────────────────────────────────────────────── */
function OperatorsList({ ops, onToast }: { ops: ReturnType<typeof useOperatorsAdmin>; onToast: (m: string) => void }) {
  const { operators, loading, error, reload, setAutoApprove, fetchPreview, saveProfile } = ops;
  const [preview, setPreview] = useState<OperatorProfilePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewNick, setPreviewNick] = useState<string>("");
  const [editFor, setEditFor] = useState<OperatorAccount | null>(null);

  const openPreview = useCallback(async (op: OperatorAccount) => {
    setPreviewNick(op.nickname);
    setPreview(null);
    setPreviewLoading(true);
    const p = await fetchPreview(op.staffId);
    setPreview(p);
    setPreviewLoading(false);
  }, [fetchPreview]);

  const onToggle = useCallback(async (op: OperatorAccount) => {
    const next = !op.autoApprove;
    const res = await setAutoApprove(op.staffId, next);
    onToast(res.ok
      ? (next ? `Авто-одобрение включено: ${op.nickname}` : `Авто-одобрение выключено: ${op.nickname}`)
      : `Ошибка: ${res.error ?? "—"}`);
  }, [setAutoApprove, onToast]);

  if (loading) {
    return (
      <div className={css.content}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={css.skel}>
            <div className={css.skelLine} style={{ width: "60%" }} />
            <div className={css.skelLine} style={{ width: "35%" }} />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className={css.emptyState}>
        <span className={css.esIco}><WarnTri /></span>
        <span className={css.esT}>Не удалось загрузить операторов</span>
        <span className={css.esS}>{error}</span>
        <button type="button" className={css.esBtn} onClick={reload}>Повторить</button>
      </div>
    );
  }

  if (operators.length === 0) {
    return (
      <div className={css.emptyState}>
        <span className={css.esIco}><NavAccounts width={40} height={40} /></span>
        <span className={css.esT}>Операторов нет</span>
      </div>
    );
  }

  return (
    <>
      <div className={css.content}>
        <div className={css.opList}>
          {operators.map((op) => (
            <article key={op.staffId} className={css.opCard}>
              <div className={css.opTop}>
                <span className={css.opAvatar}><Person /></span>
                <span className={css.opNick}>{op.nickname}</span>
                {op.characterName ? <span className={css.opChar} title="Имя персонажа (видят клиенты)">→ {op.characterName}</span> : null}
                <span className={`${css.opCardStatus} ${cardStatusCls(op.cardStatus)}`}>{cardStatusLabel(op.cardStatus)}</span>
                {op.hasPending ? (
                  <span className={css.opPending}><WarnTri /> {op.pendingCount || ""}</span>
                ) : null}
              </div>
              <div className={css.opBottom}>
                <button type="button" className={css.opCreate} onClick={() => setEditFor(op)}>
                  <Plus /> {op.cardStatus === "none" ? "Создать карточку" : "Редактировать"}
                </button>
                {op.cardStatus !== "none" ? (
                  <button type="button" className={css.opView} onClick={() => openPreview(op)}>
                    <Eye /> Как сейчас
                  </button>
                ) : null}
                <button
                  type="button"
                  className={css.opAuto}
                  aria-label="Авто-одобрение"
                  aria-pressed={op.autoApprove}
                  onClick={() => onToggle(op)}
                >
                  <span className={css.opAutoLbl}>Авто</span>
                  <span className={`${css.toggle} ${op.autoApprove ? css.on : ""}`}><span className={css.knob} /></span>
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>

      {(previewLoading || preview !== null) ? (
        <PreviewOverlay
          nick={preview?.nickname ?? previewNick}
          loading={previewLoading}
          preview={preview}
          onClose={() => { setPreview(null); setPreviewLoading(false); }}
        />
      ) : null}

      {editFor ? (
        <ProfileEditorOverlay
          op={editFor}
          sources={operators.filter((o) => o.cardStatus !== "none" && o.staffId !== editFor.staffId).map((o) => ({ staffId: o.staffId, label: o.characterName ? `${o.nickname} → ${o.characterName}` : o.nickname }))}
          fetchPreview={fetchPreview}
          onClose={() => setEditFor(null)}
          onSave={async (fields, publish) => {
            const res = await saveProfile(editFor.staffId, fields, publish);
            if (res.ok) setEditFor(null);
            onToast(res.ok ? (publish ? "Карточка опубликована" : "Черновик сохранён") : `Ошибка: ${res.error ?? "—"}`);
            return res;
          }}
        />
      ) : null}
    </>
  );
}

/* ── в) Экономика UTC — настройки начислений/штрафов (только OO) ───────────── */
const UC_GROUPS: { title: string; fields: { key: keyof UcConfig; label: string; hint: string; neg?: boolean }[] }[] = [
  {
    title: "За игры",
    fields: [
      { key: "game_completion", label: "Прохождение квеста", hint: "Начисляется за каждую сыгранную игру" },
      { key: "achievement", label: "За каждую ачивку", hint: "Добавляется к награде за игру за каждую засчитанную ачивку" },
    ],
  },
  {
    title: "Рефералы",
    fields: [
      { key: "referral_join", label: "Друг вступил в канал", hint: "Друг перешёл по личной ссылке игрока и вступил в сообщество" },
      { key: "referral_app", label: "Друг открыл приложение", hint: "Приглашённый друг впервые зашёл в мини-апп" },
      { key: "referral_clawback", label: "Возврат за ушедшего", hint: "Списывается, если друг вышел из канала в течение недели", neg: true },
    ],
  },
  {
    title: "Друзья",
    fields: [
      { key: "add_friend_to_game", label: "Пригласил друга в игру", hint: "За добавление друга в конкретный квест" },
    ],
  },
];
const UC_KEYS = UC_GROUPS.flatMap((g) => g.fields.map((f) => f.key));

/** Парсинг ввода: допускаем запятую как десятичный разделитель (рус. раскладка). */
function ucNum(raw: string | undefined): number {
  return Number(String(raw ?? "").replace(",", ".").trim());
}

function EconomyPanel({ onToast }: { onToast: (m: string) => void }) {
  const { config, loading, error, reload, save } = useUcConfig();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!config) return;
    const d: Record<string, string> = {};
    for (const k of UC_KEYS) d[k] = String(config[k]);
    setDraft(d);
  }, [config]);

  const badKey = useCallback((k: keyof UcConfig) => {
    const raw = draft[k];
    const n = ucNum(raw);
    return raw == null || raw.trim() === "" || !Number.isFinite(n) || n < 0 || n > 1000;
  }, [draft]);

  const invalid = useMemo(() => UC_KEYS.some((k) => badKey(k)), [badKey]);
  const dirty = useMemo(() => {
    if (!config) return false;
    return UC_KEYS.some((k) => ucNum(draft[k]) !== config[k]);
  }, [draft, config]);

  const onSave = useCallback(async () => {
    if (!dirty || invalid || busy) return;
    setBusy(true);
    const next: Partial<UcConfig> = {};
    for (const k of UC_KEYS) next[k] = ucNum(draft[k]);
    const res = await save(next);
    setBusy(false);
    onToast(res.ok ? "Экономика сохранена" : `Ошибка: ${res.error ?? "—"}`);
  }, [dirty, invalid, busy, draft, save, onToast]);

  const onReset = useCallback(() => {
    if (!config) return;
    const d: Record<string, string> = {};
    for (const k of UC_KEYS) d[k] = String(config[k]);
    setDraft(d);
  }, [config]);

  if (loading) {
    return (
      <div className={css.content}>
        {[0, 1].map((i) => (
          <div key={i} className={css.skel}>
            <div className={css.skelLine} style={{ width: "50%" }} />
            <div className={css.skelLine} style={{ width: "80%" }} />
          </div>
        ))}
      </div>
    );
  }
  if (error || !config) {
    return (
      <div className={css.emptyState}>
        <span className={css.esIco}><WarnTri /></span>
        <span className={css.esT}>Не удалось загрузить экономику</span>
        {error ? <span className={css.esS}>{error}</span> : null}
        <button type="button" className={css.esBtn} onClick={reload}>Повторить</button>
      </div>
    );
  }

  return (
    <div className={css.content}>
      <div className={css.ecoIntro}>
        Значения в UTC. Применяются сразу ко всему клиентскому приложению — новые начисления и
        возвраты считаются по этим числам. Диапазон 0–1000, дробные через точку или запятую (напр. 0,1).
      </div>
      {UC_GROUPS.map((g) => (
        <div key={g.title} className={css.ecoGroup}>
          <div className={css.ecoGroupH}>{g.title}</div>
          {g.fields.map((f) => (
            <div key={f.key} className={css.ecoRow}>
              <div className={css.ecoMain}>
                <div className={css.ecoLbl}>{f.label}</div>
                <div className={css.ecoHint}>{f.hint}</div>
              </div>
              <div className={css.ecoField}>
                <span className={`${css.ecoSign} ${f.neg ? css.ecoSignNeg : ""}`}>{f.neg ? "−" : "+"}</span>
                <input
                  className={`${css.ecoInput} ${badKey(f.key) ? css.ecoInputBad : ""}`}
                  type="text"
                  inputMode="decimal"
                  aria-label={f.label}
                  value={draft[f.key] ?? ""}
                  onChange={(e) => setDraft((p) => ({ ...p, [f.key]: e.target.value }))}
                />
                <span className={css.ecoUnit}>UTC</span>
              </div>
            </div>
          ))}
        </div>
      ))}
      <div className={css.ecoActions}>
        <button type="button" className={css.ecoSave} disabled={!dirty || invalid || busy} onClick={onSave}>
          {busy ? "Сохранение…" : "Сохранить"}
        </button>
        <button type="button" className={css.ecoReset} disabled={!dirty || busy} onClick={onReset}>
          Сброс
        </button>
      </div>
    </div>
  );
}

/* ── OO: выпуск «Вестника» (журнал клиентского приложения) ─────────────────── */
function MaterialsPanel({ onToast }: { onToast: (m: string) => void }) {
  const { issue, loading, error, reload, save } = useMaterialsAdmin();
  const [draft, setDraft] = useState<MaterialsIssue | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (issue) setDraft(JSON.parse(JSON.stringify(issue)) as MaterialsIssue);
  }, [issue]);

  const dirty = useMemo(
    () => !!issue && !!draft && JSON.stringify(issue) !== JSON.stringify(draft),
    [issue, draft],
  );

  const setI = (patch: Partial<MaterialsIssue>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const setCover = (patch: Partial<MaterialsIssue["cover"]>) => setDraft((d) => (d ? { ...d, cover: { ...d.cover, ...patch } } : d));
  const setEditor = (patch: Partial<MaterialsIssue["editor"]>) => setDraft((d) => (d ? { ...d, editor: { ...d.editor, ...patch } } : d));
  const setHoro = (i: number, patch: Partial<MaterialsIssue["horoscope"][number]>) =>
    setDraft((d) => (d ? { ...d, horoscope: d.horoscope.map((h, j) => (j === i ? { ...h, ...patch } : h)) } : d));
  const setReview = (patch: Partial<MaterialsIssue["review"]>) => setDraft((d) => (d ? { ...d, review: { ...d.review, ...patch } } : d));
  const setLinks = (patch: Partial<MaterialsIssue["review"]["links"]>) =>
    setDraft((d) => (d ? { ...d, review: { ...d.review, links: { ...d.review.links, ...patch } } } : d));
  const setAf = (i: number, patch: Partial<MaterialsAfisha>) =>
    setDraft((d) => (d ? { ...d, afisha: d.afisha.map((a, j) => (j === i ? { ...a, ...patch } : a)) } : d));
  const addAf = () => setDraft((d) => (d ? { ...d, afisha: [...d.afisha, { date: "", title: "", note: "" }] } : d));
  const delAf = (i: number) => setDraft((d) => (d ? { ...d, afisha: d.afisha.filter((_, j) => j !== i) } : d));
  const setObj = (i: number, patch: Partial<MaterialsObject>) =>
    setDraft((d) => (d ? { ...d, objects: d.objects.map((o, j) => (j === i ? { ...o, ...patch } : o)) } : d));
  const addObj = () => setDraft((d) => (d ? { ...d, objects: [...d.objects, { questId: "", name: "", objClass: "safe", blurb: "" }] } : d));
  const delObj = (i: number) => setDraft((d) => (d ? { ...d, objects: d.objects.filter((_, j) => j !== i) } : d));
  const setProto = (i: number, patch: Partial<MaterialsProtocol>) =>
    setDraft((d) => (d ? { ...d, protocols: d.protocols.map((p, j) => (j === i ? { ...p, ...patch } : p)) } : d));
  const addProto = () => setDraft((d) => (d ? { ...d, protocols: [...d.protocols, { code: "", title: "", terms: "" }] } : d));
  const delProto = (i: number) => setDraft((d) => (d ? { ...d, protocols: d.protocols.filter((_, j) => j !== i) } : d));

  const onSave = useCallback(async () => {
    if (!draft || !dirty || busy) return;
    setBusy(true);
    const res = await save(draft);
    setBusy(false);
    onToast(res.ok ? "Вестник сохранён" : `Ошибка: ${res.error ?? "—"}`);
  }, [draft, dirty, busy, save, onToast]);

  if (loading) {
    return (
      <div className={css.content}>
        {[0, 1].map((i) => (
          <div key={i} className={css.skel}>
            <div className={css.skelLine} style={{ width: "50%" }} />
            <div className={css.skelLine} style={{ width: "80%" }} />
          </div>
        ))}
      </div>
    );
  }
  if (error || !issue || !draft) {
    return (
      <div className={css.emptyState}>
        <span className={css.esIco}><WarnTri /></span>
        <span className={css.esT}>Не удалось загрузить «Вестник»</span>
        {error ? <span className={css.esS}>{error}</span> : null}
        <button type="button" className={css.esBtn} onClick={reload}>Повторить</button>
      </div>
    );
  }

  const lbl: CSSProperties = { display: "block", fontSize: 10.5, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--text-2)", margin: "0 0 4px" };
  const inp: CSSProperties = { width: "100%", boxSizing: "border-box", padding: "7px 9px", background: "var(--surface-1)", border: "1px solid var(--surface-3)", borderRadius: 6, color: "var(--text-1)", fontSize: 13, fontFamily: "inherit" };
  const area: CSSProperties = { ...inp, minHeight: 56, resize: "vertical", lineHeight: 1.4 };
  const field: CSSProperties = { margin: "0 0 10px" };
  const cardBox: CSSProperties = { border: "1px solid var(--surface-3)", borderRadius: 8, padding: 10, margin: "0 0 8px", background: "var(--surface-1)" };
  const miniBtn: CSSProperties = { padding: "5px 10px", borderRadius: 6, border: "1px solid var(--surface-3)", background: "var(--surface-2)", color: "var(--text-2)", fontSize: 12, cursor: "pointer" };

  const OBJ_CLASSES: ObjClass[] = ["safe", "euclid", "keter", "thaumiel"];
  const clsSelect = (val: ObjClass, on: (v: ObjClass) => void) => (
    <select style={inp} value={val} onChange={(e) => on(e.target.value as ObjClass)}>
      {OBJ_CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );

  return (
    <div className={css.content}>
      <div className={css.ecoIntro}>
        Выпуск «Вестника» в клиентском приложении. Текст и ссылки видны всем игрокам. Картинки задаются ссылкой (URL).
      </div>

      <div className={css.ecoGroup}>
        <div className={css.ecoGroupH}>Выпуск</div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ ...field, flex: "0 0 90px" }}><label style={lbl}>Номер</label><input style={inp} type="number" value={draft.number} onChange={(e) => setI({ number: Number(e.target.value) || 0 })} /></div>
          <div style={{ ...field, flex: 1 }}><label style={lbl}>Месяц</label><input style={inp} value={draft.month} placeholder="Июнь 2026" onChange={(e) => setI({ month: e.target.value })} /></div>
        </div>
        <div style={field}><label style={lbl}>Заголовок обложки</label><input style={inp} value={draft.cover.title} onChange={(e) => setCover({ title: e.target.value })} /></div>
        <div style={field}><label style={lbl}>Медиа обложки (URL, необязательно)</label><input style={inp} value={draft.cover.heroMediaUrl ?? ""} onChange={(e) => setCover({ heroMediaUrl: e.target.value })} /></div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ ...field, flex: 1 }}><label style={lbl}>Объект месяца (ID)</label><input style={inp} value={draft.objectId} onChange={(e) => setI({ objectId: e.target.value })} /></div>
          <div style={{ ...field, flex: "0 0 130px" }}><label style={lbl}>Класс</label>{clsSelect(draft.objClass, (v) => setI({ objClass: v }))}</div>
        </div>
      </div>

      <div className={css.ecoGroup}>
        <div className={css.ecoGroupH}>Слово смотрителя</div>
        <div style={field}><label style={lbl}>Автор</label><input style={inp} value={draft.editor.author} onChange={(e) => setEditor({ author: e.target.value })} /></div>
        <div style={field}><label style={lbl}>Текст</label><textarea style={{ ...area, minHeight: 90 }} value={draft.editor.text} onChange={(e) => setEditor({ text: e.target.value })} /></div>
      </div>

      <div className={css.ecoGroup}>
        <div className={css.ecoGroupH}>Звёздная сводка ({draft.horoscope.length})</div>
        {draft.horoscope.map((h, i) => (
          <div key={i} style={cardBox}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-1)", margin: "0 0 6px" }}>{h.sign || `Знак ${i + 1}`}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ ...field, flex: "0 0 110px", margin: 0 }}><label style={lbl}>Квест (ID)</label><input style={inp} value={h.questId} onChange={(e) => setHoro(i, { questId: e.target.value })} /></div>
              <div style={{ ...field, flex: 1, margin: 0 }}><label style={lbl}>Строка</label><input style={inp} value={h.line} onChange={(e) => setHoro(i, { line: e.target.value })} /></div>
            </div>
          </div>
        ))}
      </div>

      <div className={css.ecoGroup}>
        <div className={css.ecoGroupH}>Афиша ({draft.afisha.length})</div>
        {draft.afisha.map((a, i) => (
          <div key={i} style={cardBox}>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ ...field, flex: "0 0 120px" }}><label style={lbl}>Дата</label><input style={inp} value={a.date} placeholder="2026-06-30" onChange={(e) => setAf(i, { date: e.target.value })} /></div>
              <div style={{ ...field, flex: 1 }}><label style={lbl}>Заголовок</label><input style={inp} value={a.title} onChange={(e) => setAf(i, { title: e.target.value })} /></div>
            </div>
            <div style={field}><label style={lbl}>Примечание</label><input style={inp} value={a.note} onChange={(e) => setAf(i, { note: e.target.value })} /></div>
            <button type="button" style={{ ...miniBtn, color: "var(--danger, #ff6b6b)" }} onClick={() => delAf(i)}>Удалить</button>
          </div>
        ))}
        <button type="button" style={miniBtn} onClick={addAf}>+ Добавить событие</button>
      </div>

      <div className={css.ecoGroup}>
        <div className={css.ecoGroupH}>Отзыв месяца</div>
        <div style={field}><label style={lbl}>Текст</label><textarea style={area} value={draft.review.text} onChange={(e) => setReview({ text: e.target.value })} /></div>
        <div style={field}><label style={lbl}>Награда (юникоины)</label><input style={inp} type="number" value={draft.review.rewardUc} onChange={(e) => setReview({ rewardUc: Number(e.target.value) || 0 })} /></div>
        <div style={field}><label style={lbl}>2ГИС (URL)</label><input style={inp} value={draft.review.links.twogis ?? ""} onChange={(e) => setLinks({ twogis: e.target.value })} /></div>
        <div style={field}><label style={lbl}>Яндекс (URL)</label><input style={inp} value={draft.review.links.yandex ?? ""} onChange={(e) => setLinks({ yandex: e.target.value })} /></div>
        <div style={field}><label style={lbl}>Telegram (URL)</label><input style={inp} value={draft.review.links.tg ?? ""} onChange={(e) => setLinks({ tg: e.target.value })} /></div>
      </div>

      <div className={css.ecoGroup}>
        <div className={css.ecoGroupH}>Объекты ({draft.objects.length})</div>
        {draft.objects.map((o, i) => (
          <div key={i} style={cardBox}>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ ...field, flex: "0 0 110px" }}><label style={lbl}>Квест (ID)</label><input style={inp} value={o.questId} onChange={(e) => setObj(i, { questId: e.target.value })} /></div>
              <div style={{ ...field, flex: 1 }}><label style={lbl}>Название</label><input style={inp} value={o.name} onChange={(e) => setObj(i, { name: e.target.value })} /></div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ ...field, flex: "0 0 130px" }}><label style={lbl}>Класс</label>{clsSelect(o.objClass, (v) => setObj(i, { objClass: v }))}</div>
              <div style={{ ...field, flex: 1 }}><label style={lbl}>Картинка (URL)</label><input style={inp} value={o.coverUrl ?? ""} onChange={(e) => setObj(i, { coverUrl: e.target.value })} /></div>
            </div>
            <div style={field}><label style={lbl}>Описание</label><textarea style={area} value={o.blurb} onChange={(e) => setObj(i, { blurb: e.target.value })} /></div>
            <button type="button" style={{ ...miniBtn, color: "var(--danger, #ff6b6b)" }} onClick={() => delObj(i)}>Удалить</button>
          </div>
        ))}
        <button type="button" style={miniBtn} onClick={addObj}>+ Добавить объект</button>
      </div>

      <div className={css.ecoGroup}>
        <div className={css.ecoGroupH}>Протоколы ({draft.protocols.length})</div>
        {draft.protocols.map((p, i) => (
          <div key={i} style={cardBox}>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ ...field, flex: "0 0 110px" }}><label style={lbl}>Код</label><input style={inp} value={p.code} onChange={(e) => setProto(i, { code: e.target.value })} /></div>
              <div style={{ ...field, flex: 1 }}><label style={lbl}>Название</label><input style={inp} value={p.title} onChange={(e) => setProto(i, { title: e.target.value })} /></div>
            </div>
            <div style={field}><label style={lbl}>Условия</label><textarea style={area} value={p.terms} onChange={(e) => setProto(i, { terms: e.target.value })} /></div>
            <button type="button" style={{ ...miniBtn, color: "var(--danger, #ff6b6b)" }} onClick={() => delProto(i)}>Удалить</button>
          </div>
        ))}
        <button type="button" style={miniBtn} onClick={addProto}>+ Добавить протокол</button>
      </div>

      <div className={css.ecoActions}>
        <button type="button" className={css.ecoSave} disabled={!dirty || busy} onClick={onSave}>
          {busy ? "Сохранение…" : "Сохранить"}
        </button>
      </div>
    </div>
  );
}

/* ── Предпросмотр карточки оверлеем (C13) ─────────────────────────────────── */
function PreviewOverlay({
  nick, loading, preview, onClose,
}: {
  nick: string;
  loading: boolean;
  preview: OperatorProfilePreview | null;
  onClose: () => void;
}) {
  return (
    <div className={css.ovBack} role="dialog" aria-modal="true">
      <div className={css.ovHead}>
        <span className={css.ovTitle}><Eye /> {nick} · как сейчас</span>
        <button type="button" className={css.ovClose} aria-label="Закрыть" onClick={onClose}><Xmark /></button>
      </div>
      <div className={css.ovBody}>
        {loading ? (
          <div className={css.skel}>
            <div className={css.skelLine} style={{ width: "70%" }} />
            <div className={css.skelLine} style={{ width: "90%" }} />
            <div className={css.skelLine} style={{ width: "55%" }} />
          </div>
        ) : preview ? (
          (!preview.profile && (!Array.isArray(preview.media) || preview.media.length === 0)) ? (
            <div className={css.diffEmpty}>Карточка ещё не создана</div>
          ) : (
            <>
              {preview.isDraft ? <span className={css.ovDraft}><DocLines /> Черновик — ещё не опубликовано</span> : null}
              <CardPreview profile={preview.profile} news={[]} media={preview.media} />
            </>
          )
        ) : (
          <div className={css.diffEmpty}>Предпросмотр недоступен</div>
        )}
      </div>
    </div>
  );
}

/* ── Общий рендер предпросмотра карточки (профиль + медиа + новости) ──────── */
function parseNotes(v: unknown): string[] {
  try {
    const a = typeof v === "string" ? JSON.parse(v) : v;
    return Array.isArray(a) ? a.map((x) => String(x)).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function CardPreview({
  profile, news, media,
}: {
  profile: Record<string, unknown> | null | undefined;
  news?: Array<Record<string, unknown>> | null;
  media?: MediaAsset[] | null;
}) {
  // Защита от падения: оператор без карточки приходит с profile=null, а media
  // может отсутствовать. Без нормализации profile.nickname / media.find роняли
  // весь мини-апп в чёрный экран (error-boundary нет).
  const p = (profile ?? {}) as Record<string, unknown>;
  const items = Array.isArray(media) ? media : [];
  const newsList = Array.isArray(news) ? news : [];
  const name = str(p.character_name) || str(p.display_name) || str(p.nickname) || str(p.name) || "Без имени";
  const role = str(p.display_role) || str(p.character_role) || str(p.role) || "";
  const status = str(p.character_status);
  const bio = str(p.atmos_status) || str(p.blog) || str(p.description) || "";
  const templeTitle = str(p.temple_title);
  const templeBlurb = str(p.temple_blurb);
  const specialText = str(p.special_text);
  const specialOpens = str(p.special_opens_at);
  const trailer = str(p.trailer_src);
  const notes = parseNotes(p.archive_notes);

  const avatar = useMemo(() => items.find((m) => m.kind === "photo") ?? items[0], [items]);
  const tiles = useMemo(() => items.slice(0, 8), [items]);

  return (
    <div className={css.preview}>
      <div className={css.pvHero}>
        <div className={css.pvAvatar}>
          {avatar?.posterUrl || avatar?.streamUrl ? (
            <img src={avatar.posterUrl ?? avatar.streamUrl ?? ""} alt="" style={cropStyle(avatar, "dossier_hero")} />
          ) : (
            <span className={css.pvAvatarPh}><Person /></span>
          )}
        </div>
        <div className={css.pvMeta}>
          <div className={css.pvName}>{name}</div>
          {role ? <div className={css.pvRole}>{role}</div> : null}
          {status ? <div className={css.pvStatus}>{status}</div> : null}
        </div>
      </div>

      {bio ? (
        <div className={css.pvSection}>
          <div className={css.pvSecH}>Описание персонажа</div>
          <div className={css.pvBio}>{bio}</div>
        </div>
      ) : null}

      {tiles.length > 0 ? (
        <div className={css.pvSection}>
          <div className={css.pvSecH}>Медиа</div>
          <div className={css.pvTiles}>
            {tiles.map((m) => (
              <div key={m.id} className={css.pvTile}>
                {m.thumbKey || m.posterUrl || m.streamUrl ? (
                  <img src={m.posterUrl ?? m.streamUrl ?? ""} alt={m.label} style={cropStyle(m, "media_tile")} />
                ) : (
                  <span className={css.pvTilePh}>{m.kind === "video" ? <VideoIco /> : <Photo />}</span>
                )}
                {m.kind === "video" ? <span className={css.pvTileVid}><VideoIco /></span> : null}
                {m.fanOnly ? <span className={css.pvTileBadge}><Lock /></span> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {(templeTitle || templeBlurb) ? (
        <div className={css.pvSection}>
          <div className={css.pvFan}>
            <div className={css.pvFanTitle}>{templeTitle || "Фан-клуб"}</div>
            {templeBlurb ? <div className={css.pvFanBlurb}>{templeBlurb}</div> : null}
          </div>
        </div>
      ) : null}

      {specialText ? (
        <div className={css.pvSpecial}>
          <span className={css.lbl}>Спецверсия{specialOpens ? ` · ${ddmm(specialOpens)}` : ""}</span>
          {specialText}
        </div>
      ) : null}

      {trailer ? (
        <div className={css.pvSection}>
          <div className={css.pvSecH}><News /> Трейлер</div>
          <div className={css.pvBlog}>{trailer}</div>
        </div>
      ) : null}

      {newsList.length > 0 ? (
        <div className={css.pvSection}>
          <div className={css.pvSecH}>Новости</div>
          <div className={css.pvNews}>
            {newsList.map((n, i) => (
              <div key={i} className={css.pvNewsItem}>
                <div className={css.pvNewsTitle}>{str(n.title) || "Без заголовка"}</div>
                {str(n.body) ? <div className={css.pvNewsBody}>{str(n.body)}</div> : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {notes.length > 0 ? (
        <div className={css.pvSection}>
          <div className={css.pvSecH}>Пометки на полях</div>
          <ul className={css.pvNotes}>
            {notes.map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/* ── Полный редактор карточки персонажа (OO создаёт/редактирует/публикует) ─── */
function ProfileEditorOverlay({
  op, sources, fetchPreview, onClose, onSave,
}: {
  op: OperatorAccount;
  sources: { staffId: string; label: string }[];
  fetchPreview: (staffId: string) => Promise<OperatorProfilePreview | null>;
  onClose: () => void;
  onSave: (fields: Record<string, unknown>, publish: boolean) => Promise<{ ok: boolean; error?: string }>;
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState<Record<string, string | number>>({ is_public: 1 });

  useEffect(() => {
    let alive = true;
    (async () => {
      const p = await fetchPreview(op.staffId);
      if (!alive) return;
      const pr = (p?.profile ?? {}) as Record<string, unknown>;
      const s = (k: string, d = "") => (pr[k] == null ? d : String(pr[k]));
      setF({
        character_name: s("character_name", op.characterName || ""),
        display_role: s("display_role"),
        character_status: s("character_status"),
        atmos_status: s("atmos_status"),
        trailer_src: s("trailer_src"),
        temple_title: s("temple_title"),
        temple_blurb: s("temple_blurb"),
        special_text: s("special_text"),
        special_opens_at: s("special_opens_at"),
        is_public: pr.is_public == null ? 1 : (pr.is_public ? 1 : 0),
      });
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [op.staffId, op.characterName, fetchPreview]);

  const set = (k: string, v: string | number) => setF((p) => ({ ...p, [k]: v }));
  const [copied, setCopied] = useState("");
  // «Привязать к существующей карточке» = перенести персону из уже заполненной
  // карточки другого оператора (быстрый старт; имя персонажа потом можно поправить).
  const copyFrom = async (sourceId: string) => {
    if (!sourceId) return;
    const p = await fetchPreview(sourceId);
    const pr = (p?.profile ?? {}) as Record<string, unknown>;
    const g = (k: string, d = "") => (pr[k] == null ? d : String(pr[k]));
    setF((cur) => ({
      ...cur,
      character_name: g("character_name", String(cur.character_name ?? "")),
      display_role: g("display_role"),
      character_status: g("character_status"),
      atmos_status: g("atmos_status"),
      trailer_src: g("trailer_src"),
      temple_title: g("temple_title"),
      temple_blurb: g("temple_blurb"),
      special_text: g("special_text"),
      special_opens_at: g("special_opens_at"),
    }));
    setCopied(sources.find((x) => x.staffId === sourceId)?.label ?? "");
  };
  const canSave = String(f.character_name ?? "").trim().length > 0 && !busy && !loading;
  const submit = async (publish: boolean) => {
    if (!canSave) return;
    setBusy(true);
    await onSave(f, publish);
    setBusy(false);
  };

  const TEXT: [string, string][] = [
    ["character_name", "Имя персонажа *"],
    ["display_role", "Роль персонажа"],
    ["character_status", "Статус персонажа"],
    ["trailer_src", "Трейлер (ссылка на видео)"],
    ["temple_title", "Храм — заголовок"],
    ["special_opens_at", "Спецверсия — когда"],
  ];
  const AREA: [string, string][] = [
    ["atmos_status", "Блог / описание"],
    ["temple_blurb", "Храм — текст"],
    ["special_text", "Спецверсия — текст"],
  ];

  return (
    <div className={css.ovBack} role="dialog" aria-modal="true">
      <div className={css.ovHead}>
        <span className={css.ovTitle}>{op.cardStatus === "none" ? "Новая карточка" : "Редактирование"} · {op.nickname}</span>
        <button type="button" className={css.ovClose} aria-label="Закрыть" onClick={onClose}><Xmark /></button>
      </div>
      <div className={css.ovBody}>
        {loading ? (
          <div className={css.skel}><div className={css.skelLine} style={{ width: "60%" }} /><div className={css.skelLine} style={{ width: "90%" }} /></div>
        ) : (
          <>
            <div className={css.edHint}>
              Клиенты видят имя персонажа, не настоящее имя сотрудника. Фото и медиа берутся из того,
              что оператор загрузил у себя в «Анкете». «Опубликовать» — карточка сразу видна клиентам.
            </div>
            {sources.length > 0 ? (
              <label className={css.edLbl}>Привязать к существующей карточке (скопировать персону)
                <select className={css.mInput} value="" onChange={(e) => { copyFrom(e.target.value); }}>
                  <option value="">— выбрать карточку —</option>
                  {sources.map((src) => (
                    <option key={src.staffId} value={src.staffId}>{src.label}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {copied ? <div className={css.edHint}>Скопировано из «{copied}» — поправь имя персонажа при необходимости.</div> : null}
            {TEXT.map(([k, label]) => (
              <label key={k} className={css.edLbl}>{label}
                <input className={css.mInput} value={String(f[k] ?? "")} onChange={(e) => set(k, e.target.value)} />
              </label>
            ))}
            {AREA.map(([k, label]) => (
              <label key={k} className={css.edLbl}>{label}
                <textarea className={css.mTextarea} value={String(f[k] ?? "")} onChange={(e) => set(k, e.target.value)} />
              </label>
            ))}
            <button type="button" className={css.edToggle} onClick={() => set("is_public", f.is_public ? 0 : 1)}>
              <span>Видна клиентам</span>
              <span className={`${css.toggle} ${f.is_public ? css.on : ""}`}><span className={css.knob} /></span>
            </button>
          </>
        )}
      </div>
      <div className={css.edFoot}>
        <button type="button" className={`${css.mBtn} ${css.mCancel}`} onClick={onClose}>Отмена</button>
        <button type="button" className={css.mBtn} disabled={!canSave} onClick={() => submit(false)}>Черновик</button>
        <button type="button" className={`${css.mBtn} ${css.mGo}`} disabled={!canSave} onClick={() => submit(true)}>Опубликовать</button>
      </div>
    </div>
  );
}

/* ── Причина (reject / needs-revision) — кастомная модалка (A3) ───────────── */
function NoteModal({
  kind, onCancel, onSubmit,
}: {
  kind: "reject" | "revision";
  onCancel: () => void;
  onSubmit: (note: string) => void | Promise<void>;
}) {
  const presets = kind === "reject" ? REJECT_PRESETS : REVISION_PRESETS;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const note = useMemo(() => [[...picked].join(", "), text.trim()].filter(Boolean).join(". ").trim(), [picked, text]);

  const submit = useCallback(async () => {
    if (!note || busy) return;
    setBusy(true);
    await onSubmit(note);
  }, [note, busy, onSubmit]);

  return (
    <div className={css.modalHost} role="dialog" aria-modal="true">
      <div className={css.modalBackdrop} onClick={onCancel} />
      <div className={`${css.modal} ${kind === "revision" ? css.modalRevise : ""}`}>
        <div className={css.mTitle}>{kind === "reject" ? "Отклонить заявку" : "Вернуть на доработку"}</div>
        <div className={css.mBody}>Причина уйдёт оператору — он увидит её в своём кабинете.</div>
        <div className={css.mPresets}>
          {presets.map((p) => (
            <button
              key={p}
              type="button"
              className={`${css.mPreset} ${picked.has(p) ? css.on : ""}`}
              onClick={() => setPicked((s) => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; })}
            >
              {p}
            </button>
          ))}
        </div>
        <textarea
          className={css.mTextarea}
          value={text}
          placeholder={kind === "reject" ? "Почему отклоняете…" : "Что нужно исправить…"}
          onChange={(e) => setText(e.target.value)}
        />
        <div className={css.mActions}>
          <button type="button" className={`${css.mBtn} ${css.mCancel}`} onClick={onCancel}>Отмена</button>
          <button type="button" className={`${css.mBtn} ${kind === "reject" ? css.mDanger : css.mGo}`} disabled={!note || busy} onClick={submit}>
            {kind === "reject" ? "Отклонить" : "На доработку"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Confirm модалка (массовое одобрение) ─────────────────────────────────── */
function NoteConfirm({
  title, body, confirmLabel, busy, onCancel, onConfirm,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  return (
    <div className={css.modalHost} role="dialog" aria-modal="true">
      <div className={css.modalBackdrop} onClick={onCancel} />
      <div className={`${css.modal} ${css.modalRevise}`}>
        <div className={css.mTitle} style={{ color: "var(--green)" }}>{title}</div>
        <div className={css.mBody}>{body}</div>
        <div className={css.mActions}>
          <button type="button" className={`${css.mBtn} ${css.mCancel}`} onClick={onCancel}>Отмена</button>
          <button type="button" className={`${css.mBtn} ${css.bbAccept}`} disabled={busy} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────────────── */
function changesWord(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "правка";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "правки";
  return "правок";
}

function str(v: unknown): string {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}
function isEmpty(v: unknown): boolean {
  return v == null || v === "" || (Array.isArray(v) && v.length === 0);
}
function fmtVal(v: unknown): string {
  if (isEmpty(v)) return "— пусто —";
  if (Array.isArray(v)) return v.map(fmtVal).join(", ");
  if (typeof v === "object") {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  if (typeof v === "boolean") return v ? "да" : "нет";
  return String(v);
}

const FIELD_LABELS: Record<string, string> = {
  nickname: "Имя",
  display_name: "Имя",
  display_role: "Роль персонажа",
  character_role: "Роль персонажа",
  atmos_status: "Блог / описание",
  blog: "Блог / описание",
  description: "Описание",
  photo_url: "Фото профиля",
  avatar: "Аватар",
  trailer_src: "Трейлер",
  special_text: "Спецверсия (текст)",
  special_opens_at: "Спецверсия (дата)",
  is_public: "Публичность",
  is_draft: "Черновик",
};
function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/_/g, " ");
}

function summaryIcon(s: string): ReactElement {
  const low = s.toLowerCase();
  if (/фото|аватар|обложк/.test(low)) return <Photo />;
  if (/видео|трейлер/.test(low)) return <VideoIco />;
  if (/новост/.test(low)) return <News />;
  if (/блог|описан|текст/.test(low)) return <DocLines />;
  return <DocLines />;
}

function cardStatusLabel(s: CardStatus): string {
  return s === "published" ? "опубликована" : s === "draft" ? "черновик" : "нет карточки";
}
function cardStatusCls(s: CardStatus): string {
  return (s === "published" ? css.cardPublished : s === "draft" ? css.cardDraft : css.cardNone) ?? "";
}

/**
 * CSS-кроп ассета под usage (нормализованные {x,y,w,h} 0..1) + фильтр-пресет.
 * Telegram-файл неизменяем (§0.1.1) — кроп/фильтр накладываем на клиенте.
 */
function cropStyle(asset: MediaAsset | undefined, usage: string): React.CSSProperties {
  if (!asset) return {};
  const crop = asset.crops?.[usage];
  const style: React.CSSProperties = { objectFit: "cover" };
  if (crop && crop.w > 0 && crop.h > 0) {
    const scale = 1 / crop.w;
    style.transformOrigin = "top left";
    style.transform = `scale(${scale})`;
    style.objectPosition = `${crop.x * 100}% ${crop.y * 100}%`;
  }
  const preset = str(asset.filter?.preset);
  if (preset === "archive") style.filter = "grayscale(0.35) sepia(0.2) contrast(1.05)";
  return style;
}
